import { CheckIcon, MagnifyingGlass, X, WarningCircle } from '@phosphor-icons/react';
import { Button, Modal, TextInput } from 'flowbite-react';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { APP_PLATFORM_LOGO_MAP } from '../../../../shared/constants';
import type { IIAPApp, IIAPPlanLimit } from '../../../../interfaces';
import { useSelectAppleIAPApps } from '../../../../hooks/post/useSelectAppleIAPApps';
import { useSelectGoogleIAPApps } from '../../../../hooks/post/useSelectGoogleIAPApps';

interface AppSelectionModalProps {
    isOpen: boolean;
    onClose: () => void;
    platform: 'apple' | 'google';
    apps: IIAPApp[];
    planLimit: IIAPPlanLimit;
    socialAccountId: number;
    onSuccess: (selectedIds: string[]) => void;
    initialSelectedIds?: string[];
}

const PLATFORM_CONFIG = {
    apple: {
        name: 'Apple IAP',
        icon: APP_PLATFORM_LOGO_MAP.apple,
        iconClass: 'bg-gray-900 invert',
    },
    google: {
        name: 'Google IAP',
        icon: APP_PLATFORM_LOGO_MAP.playStore,
        iconClass: '',
    },
};

const AppSelectionModal: React.FC<AppSelectionModalProps> = ({
    isOpen,
    onClose,
    platform,
    apps,
    planLimit,
    socialAccountId,
    onSuccess,
    initialSelectedIds,
}) => {
    const { t } = useTranslation();
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set(initialSelectedIds ?? []));
    const [searchQuery, setSearchQuery] = useState('');
    const [apiError, setApiError] = useState<string | null>(null);

    const appleSelectMutation = useSelectAppleIAPApps();
    const googleSelectMutation = useSelectGoogleIAPApps();
    const selectMutation = platform === 'apple' ? appleSelectMutation : googleSelectMutation;

    const maxSelectable = planLimit.remaining_slots === -1 ? Infinity : planLimit.remaining_slots;
    const config = PLATFORM_CONFIG[platform];

    const filteredApps = useMemo(() => {
        if (!searchQuery.trim()) return apps;
        const q = searchQuery.toLowerCase();
        return apps.filter(
            (app) =>
                app.name?.toLowerCase().includes(q) ||
                app.id?.toLowerCase().includes(q) ||
                app.bundleId?.toLowerCase().includes(q) ||
                app.package_name?.toLowerCase().includes(q),
        );
    }, [apps, searchQuery]);

    const toggleApp = (id: string) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else if (next.size < maxSelectable) {
                next.add(id);
            }
            return next;
        });
        setApiError(null);
    };

    const handleConfirm = () => {
        setApiError(null);
        selectMutation.mutate(
            {
                social_account_id: socialAccountId,
                active_app_ids: Array.from(selectedIds),
            },
            {
                onSuccess: () => onSuccess(Array.from(selectedIds)),
                onError: (err: unknown) => {
                    const apiErr = err instanceof Error ? err as { message?: string; response?: { data?: { error?: string } } } : null;
                    const msg =
                        (apiErr as { response?: { data?: { error?: string } } } | null)?.response?.data?.error ||
                        apiErr?.message ||
                        t('configurations.integrations.appSelection.error', 'Failed to save app selection. Please try again.');
                    setApiError(msg);
                },
            },
        );
    };

    const handleClose = () => {
        setSelectedIds(new Set());
        setSearchQuery('');
        setApiError(null);
        onClose();
    };

    const isUnlimited = planLimit.remaining_slots === -1;
    const remainingAfterSelection = isUnlimited ? null : planLimit.remaining_slots - selectedIds.size;

    return (
        <Modal show={isOpen} onClose={handleClose} size="md" popup>
            <div className="relative bg-white rounded-2xl p-6">
                {/* Close Button */}
                <button
                    onClick={handleClose}
                    className="absolute top-4 right-4 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                >
                    <X size={20} weight="bold" />
                </button>

                {/* Header */}
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg overflow-hidden flex items-center justify-center bg-gray-50 border border-gray-100 flex-shrink-0">
                        <img
                            src={config.icon}
                            alt={config.name}
                            className={`w-7 h-7 object-contain ${config.iconClass}`}
                        />
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                            {t('configurations.integrations.appSelection.title', 'Select Apps to Sync')}
                        </h3>
                        <p className="text-xs text-gray-500">{config.name}</p>
                    </div>
                </div>

                {/* Plan cap badge */}
                <div className="flex items-center justify-between gap-2 mb-4 p-2.5 rounded-xl bg-indigo-50 border border-indigo-100">
                    <span className="text-xs font-semibold text-indigo-700">
                        {selectedIds.size} / {isUnlimited ? apps.length : planLimit.max_apps} apps selected
                    </span>
                    <span className="text-xs font-medium text-indigo-500">
                        {isUnlimited
                            ? t('configurations.integrations.appSelection.unlimited', 'Select as many apps as you like')
                            : t(
                                'configurations.integrations.appSelection.slotsInfo',
                                'Your plan allows up to {{max}} apps · {{remaining}} slot(s) remaining',
                                { max: planLimit.max_apps, remaining: remainingAfterSelection },
                            )}
                    </span>
                </div>

                {/* Search */}
                <div className="mb-3">
                    <TextInput
                        icon={MagnifyingGlass}
                        placeholder={t('configurations.integrations.appSelection.search', 'Search apps…')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full"
                        sizing="sm"
                    />
                </div>

                {/* App list */}
                <div className="max-h-60 overflow-y-auto space-y-2 mb-4 pr-0.5">
                    {filteredApps.length > 0 ? (
                        filteredApps.map((app) => {
                            const isSelected = selectedIds.has(app.id);
                            const isDisabled = !isSelected && selectedIds.size >= maxSelectable;
                            const subLabel = app.bundleId || app.package_name || app.id;

                            return (
                                <button
                                    key={app.id}
                                    onClick={() => !isDisabled && toggleApp(app.id)}
                                    disabled={isDisabled}
                                    className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left
                                        ${isSelected
                                            ? 'border-emerald-500 bg-emerald-50'
                                            : isDisabled
                                                ? 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed'
                                                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                        }`}
                                >
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-semibold text-gray-800 truncate">{app.name}</p>
                                        <p className="text-xs text-gray-400 truncate font-mono">{subLabel}</p>
                                    </div>
                                    <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 border transition-colors
                                        ${isSelected ? 'bg-emerald-500 border-emerald-500' : 'bg-white border-gray-300'}`}
                                    >
                                        {isSelected && <CheckIcon size={11} weight="bold" className="text-white" />}
                                    </div>
                                </button>
                            );
                        })
                    ) : (
                        <div className="text-center py-8 text-gray-400 text-sm">
                            {searchQuery
                                ? t('configurations.integrations.appSelection.noMatch', 'No apps match your search')
                                : t('configurations.integrations.appSelection.noApps', 'No apps found')}
                        </div>
                    )}
                </div>

                {/* API error */}
                {apiError && (
                    <div className="flex items-start gap-2 mb-3 p-3 rounded-xl bg-red-50 border border-red-200">
                        <WarningCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-red-700">{apiError}</p>
                    </div>
                )}

                {/* Footer */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <Button color="light" onClick={handleClose} disabled={selectMutation.isPending}>
                        {t('common.cancel')}
                    </Button>
                    <Button
                        color="success"
                        onClick={handleConfirm}
                        disabled={selectedIds.size === 0 || selectMutation.isPending}
                        isProcessing={selectMutation.isPending}
                    >
                        {t('configurations.integrations.appSelection.confirm', 'Confirm Selection')}
                        {selectedIds.size > 0 && (
                            <span className="ml-2 bg-white/20 text-white text-xs font-bold px-1.5 py-0.5 rounded-full">
                                {selectedIds.size}
                            </span>
                        )}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default AppSelectionModal;
